<template>
  <div class="custom-button-dialog">
    <v-form ref="form" v-model="isFormValid" class="edit-column">
      <div class="section">
        <v-autocomplete
          v-model="selectedPreset"
          :items="presetItems"
          :label="$t('shortcodes.custom_button.preset')"
          :menu-props="{ offsetY: true }"
          clearable
          hide-details
          @change="applyPreset" />
      </div>

      <div class="section">
        <h4 class="mb-4">{{ $t("shortcodes.custom_button.content") }}</h4>

        <v-row dense>
          <v-col cols="8">
            <v-text-field
              v-model="formData.text"
              :label="requiredLabel('button_text')"
              :placeholder="$t('shortcodes.custom_button.button_text')"
              :rules="requiredRules('button_text_required')"
              required />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="formData.fontSize"
              :label="$t('shortcodes.custom_button.font_size')"
              type="number"
              min="1"
              :rules="positiveRules" />
          </v-col>
        </v-row>

        <v-select
          v-model="formData.fontFamily"
          :items="fontFamilyOptions"
          :label="$t('shortcodes.custom_button.font_family')"
          :menu-props="{ offsetY: true }"
          clearable
          class="mt-2" />

        <v-switch v-model="formData.enableNoWrap" :label="$t('shortcodes.custom_button.enable_no_wrap')" class="mt-0" />

        <v-switch
          v-model="formData.enableSecondLine"
          :label="$t('shortcodes.custom_button.add_second_line')"
          class="mt-0" />

        <template v-if="formData.enableSecondLine">
          <v-row dense>
            <v-col cols="8">
              <v-text-field
                v-model="formData.secondaryText"
                :label="requiredLabel('second_line')"
                :placeholder="$t('shortcodes.custom_button.second_line')"
                :rules="requiredRules('second_line_required')"
                required />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="formData.secondaryFontSize"
                :label="$t('shortcodes.custom_button.font_size')"
                type="number"
                min="1"
                :rules="positiveRules" />
            </v-col>
          </v-row>

          <v-text-field
            v-model="formData.lineGap"
            :label="$t('shortcodes.custom_button.line_gap')"
            type="number"
            min="0"
            :rules="nonNegativeRules" />
        </template>

        <v-text-field
          v-model="formData.url"
          :label="requiredLabel('url')"
          placeholder="https://domain.com"
          :rules="requiredRules('url_required')"
          required />

        <v-select
          v-model="formData.openLink"
          :items="openLinkOptions"
          :label="$t('shortcodes.custom_button.open_link')"
          :menu-props="{ offsetY: true }" />
      </div>

      <div class="section">
        <h4 class="mb-4">{{ $t("shortcodes.custom_button.icon") }}</h4>

        <v-switch v-model="formData.enableIcon" :label="$t('shortcodes.custom_button.enable_icon')" class="mt-0" />

        <template v-if="formData.enableIcon">
          <v-btn-toggle v-model="formData.icon" class="va-btn-toggle mb-2" mandatory tile color="primary" group>
            <v-btn value="check">{{ $t("shortcodes.custom_button.icon_check") }}</v-btn>
            <v-btn value="arrow">{{ $t("shortcodes.custom_button.icon_arrow") }}</v-btn>
            <v-btn value="phone">{{ $t("shortcodes.custom_button.icon_phone") }}</v-btn>
            <v-btn value="custom">{{ $t("shortcodes.custom_button.icon_upload") }}</v-btn>
          </v-btn-toggle>

          <VaImageDragAndDropInput
            v-if="formData.icon === 'custom'"
            :label="$t('shortcodes.custom_button.icon_file')"
            :item-id="formData.customIconId"
            :required="true"
            :display-errors="validationRequested"
            @update:photo="updateIcon" />

          <v-text-field
            v-model="formData.iconGap"
            :label="$t('shortcodes.custom_button.icon_gap')"
            type="number"
            min="0"
            :rules="nonNegativeRules" />
          <v-text-field
            v-model="formData.iconSize"
            :label="$t('shortcodes.custom_button.icon_size')"
            type="number"
            min="1"
            :rules="positiveRules" />
          <VaColorPicker
            :value="formData.iconColor"
            :title="$t('shortcodes.custom_button.icon_color')"
            position="bottom"
            class="mb-4"
            @input="setColor('iconColor', $event)" />
        </template>
      </div>

      <div class="section">
        <h4 class="mb-4">{{ $t("shortcodes.custom_button.appearance") }}</h4>

        <VaColorPicker
          :value="formData.bgColor"
          :title="$t('shortcodes.custom_button.button_color')"
          position="bottom"
          class="mb-4"
          @input="setColor('bgColor', $event)" />
        <VaColorPicker
          :value="formData.textColor"
          :title="$t('shortcodes.custom_button.text_color')"
          position="bottom"
          class="mb-4"
          @input="setColor('textColor', $event)" />
        <VaColorPicker
          v-if="formData.enableSecondLine"
          :value="formData.secondaryTextColor"
          :title="$t('shortcodes.custom_button.second_line_color')"
          position="bottom"
          class="mb-4"
          @input="setColor('secondaryTextColor', $event)" />

        <v-switch v-model="formData.enableHover" :label="$t('shortcodes.custom_button.enable_hover')" class="mt-0" />

        <VaColorPicker
          v-if="formData.enableHover"
          :value="formData.hoverColor"
          :title="$t('shortcodes.custom_button.hover_color')"
          position="bottom"
          class="mb-4"
          @input="setColor('hoverColor', $event)" />

        <v-switch v-model="formData.enableBorder" :label="$t('shortcodes.custom_button.enable_border')" class="mt-0" />

        <template v-if="formData.enableBorder">
          <VaColorPicker
            :value="formData.borderColor"
            :title="$t('shortcodes.custom_button.border_color')"
            position="bottom"
            class="mb-4"
            @input="setColor('borderColor', $event)" />
          <v-text-field
            v-model="formData.borderWidth"
            :label="$t('shortcodes.custom_button.border_width')"
            type="number"
            min="0"
            :rules="nonNegativeRules" />
        </template>

        <v-text-field
          v-model="formData.borderRadius"
          :label="$t('shortcodes.custom_button.border_radius')"
          type="number"
          min="0"
          :rules="nonNegativeRules" />
        <v-text-field
          v-model="formData.paddingY"
          :label="$t('shortcodes.custom_button.padding_y')"
          type="number"
          min="0"
          :rules="nonNegativeRules" />
        <v-text-field
          v-model="formData.paddingX"
          :label="$t('shortcodes.custom_button.padding_x')"
          type="number"
          min="0"
          :rules="nonNegativeRules" />

        <v-switch
          v-model="formData.enableFullWidth"
          :label="$t('shortcodes.custom_button.enable_full_width')"
          class="mt-0" />

        <v-text-field
          v-if="!formData.enableFullWidth"
          v-model="formData.width"
          :label="$t('shortcodes.custom_button.width')"
          type="number"
          min="1"
          :rules="positiveRules" />

        <v-switch v-model="formData.enable3d" :label="$t('shortcodes.custom_button.enable_3d')" class="mt-0" />

        <template v-if="formData.enable3d">
          <v-row dense>
            <v-col cols="4">
              <v-text-field
                v-model="formData.shadowX"
                :label="$t('shortcodes.custom_button.shadow_x')"
                type="number"
                :rules="anyNumberRules" />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="formData.shadowY"
                :label="$t('shortcodes.custom_button.shadow_y')"
                type="number"
                :rules="anyNumberRules" />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="formData.shadowBlur"
                :label="$t('shortcodes.custom_button.shadow_blur')"
                type="number"
                min="0"
                :rules="nonNegativeRules" />
            </v-col>
          </v-row>
          <VaColorPicker
            :value="formData.shadowColor"
            :title="$t('shortcodes.custom_button.shadow_color')"
            position="bottom"
            class="mb-4"
            @input="setColor('shadowColor', $event)" />
        </template>
      </div>

      <div class="section">
        <h4 class="mb-4">{{ $t("shortcodes.custom_button.options") }}</h4>

        <v-switch v-model="formData.enablePulse" :label="$t('shortcodes.custom_button.enable_pulse')" class="mt-0" />
        <v-switch v-model="formData.enableAd" :label="$t('shortcodes.custom_button.enable_ad')" class="mt-0" />
      </div>
    </v-form>

    <div class="preview-column">
      <div class="section preview">
        <h4 class="mb-4">{{ $t("shortcodes.custom_button.preview") }}</h4>
        <a :class="preview.classNames" :style="preview.style" href="#" @click.prevent>
          <span v-if="preview.iconHtml" class="custom-button__icon-slot" v-html="preview.iconHtml" />
          <span class="custom-button__content">
            <span class="custom-button__text">{{ preview.text }}</span>
            <span
              v-if="preview.secondaryText"
              class="custom-button__secondary"
              :style="{ color: preview.secondaryTextColor }"
              >{{ preview.secondaryText }}</span
            >
          </span>
          <span v-if="preview.enableAd" class="custom-button__ad">Ad</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    getCustomButtonSecondaryText,
    getCustomButtonClassNames,
    getCustomButtonStyle,
    getCustomButtonIconMarkup,
  } from "@/assets/utilities/tinymce/shortcodeConfigs/customButtonConfig";
  import { CUSTOM_BUTTON_PRESETS } from "@/assets/utilities/tinymce/shortcodes/customButtonPresets";
  import { CUSTOM_BUTTON_EVENT_TYPES, FONT_FAMILIES } from "@/constant";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  import VaImageDragAndDropInput from "@/components/custom/VaImageDragAndDropInput";

  const CUSTOM_BUTTON_DEFAULTS = {
    text: "",
    enableSecondLine: false,
    secondaryText: "",
    secondaryFontSize: "14",
    secondaryTextColor: "#ffffff",
    lineGap: "0",
    enableIcon: false,
    icon: "check",
    customIconId: "",
    customIconSrc: "",
    iconGap: "8",
    iconSize: "22",
    iconColor: "#ffffff",
    url: "",
    openLink: "_self",
    bgColor: "#28b02b",
    textColor: "#ffffff",
    enableHover: false,
    hoverColor: "#28b02b",
    enableBorder: false,
    borderColor: "#28b02b",
    borderWidth: "2",
    borderRadius: "40",
    fontFamily: "",
    fontSize: "22",
    enableNoWrap: false,
    paddingY: "20",
    paddingX: "32",
    enableFullWidth: false,
    width: "400",
    enablePulse: false,
    enableAd: false,
    enable3d: false,
    shadowX: "6",
    shadowY: "6",
    shadowBlur: "0",
    shadowColor: "#1a7d1e",
  };

  const withCustomButtonDefaults = (options = {}) => {
    const data = { ...CUSTOM_BUTTON_DEFAULTS, ...options };

    Object.keys(CUSTOM_BUTTON_DEFAULTS).forEach(key => {
      if (data[key] === "" || data[key] === null || data[key] === undefined) {
        data[key] = CUSTOM_BUTTON_DEFAULTS[key];
      }
    });

    return data;
  };

  const isNumber = value => /^-?\d+(\.\d+)?$/.test(String(value));
  const isNonNegativeNumber = value => isNumber(value) && Number(value) >= 0;
  const isPositiveNumber = value => isNumber(value) && Number(value) > 0;

  export default {
    name: "CustomButtonDialog",
    components: { VaColorPicker, VaImageDragAndDropInput },
    data() {
      return {
        isFormValid: false,
        selectedPreset: null,
        validationRequested: false,
        fontFamilyOptions: FONT_FAMILIES,
        formData: { ...CUSTOM_BUTTON_DEFAULTS },
        openLinkOptions: [
          { value: "_self", text: this.$t("shortcodes.custom_button.same_tab") },
          { value: "_blank", text: this.$t("shortcodes.custom_button.new_tab") },
          { value: "_top", text: this.$t("shortcodes.custom_button.top_window") },
        ],
      };
    },
    computed: {
      presetItems() {
        return CUSTOM_BUTTON_PRESETS.map(preset => ({ value: preset.id, text: preset.name }));
      },
      preview() {
        const data = withCustomButtonDefaults({
          ...this.formData,
          text: this.formData.text.trim() || this.$t("shortcodes.custom_button.button_text"),
        });

        return {
          classNames: getCustomButtonClassNames(data),
          style: getCustomButtonStyle(data),
          iconHtml: getCustomButtonIconMarkup(data),
          text: data.text,
          secondaryText: getCustomButtonSecondaryText(data),
          secondaryTextColor: data.secondaryTextColor,
          enableAd: data.enableAd,
        };
      },
      isIconValid() {
        return !this.formData.enableIcon || this.formData.icon !== "custom" || !!this.formData.customIconSrc;
      },
      isValid() {
        return this.isFormValid && this.isIconValid;
      },
      nonNegativeRules() {
        return this.numberRules(isNonNegativeNumber, "value_invalid_non_negative");
      },
      positiveRules() {
        return this.numberRules(isPositiveNumber, "value_invalid_positive");
      },
      anyNumberRules() {
        return this.numberRules(isNumber, "value_invalid_number");
      },
    },
    watch: {
      isValid() {
        this.sendValidation();
      },
      "formData.bgColor"(value, oldValue) {
        if (this.formData.hoverColor === oldValue) this.formData.hoverColor = value;
      },
      formData: {
        handler() {
          this.validateAndUpdate();
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    methods: {
      requiredLabel(key) {
        return `${this.$t(`shortcodes.custom_button.${key}`)} *`;
      },
      requiredRules(messageKey) {
        return [v => !!(v && v.trim()) || this.$t(`shortcodes.custom_button.${messageKey}`)];
      },
      numberRules(isAllowed, invalidKey) {
        return [v => !String(v ?? "").trim() || isAllowed(v) || this.$t(`shortcodes.custom_button.${invalidKey}`)];
      },
      applyPreset(presetId) {
        const preset = CUSTOM_BUTTON_PRESETS.find(item => item.id === presetId);
        if (!preset) return;

        const current = this.formData;
        const next = {
          ...CUSTOM_BUTTON_DEFAULTS,
          ...preset.values,
        };
        next.text = current.text || next.text;
        next.secondaryText = current.secondaryText || next.secondaryText;
        next.url = current.url || next.url;
        next.openLink = current.openLink || next.openLink;
        next.fontFamily = current.fontFamily || next.fontFamily;
        next.enableFullWidth = current.enableFullWidth;
        next.enableNoWrap = current.enableNoWrap;
        next.width = preset.values.width || current.width;

        if (!preset.values.hoverColor && preset.values.bgColor) {
          next.hoverColor = preset.values.bgColor;
        }

        this.formData = next;
      },

      async validateAndUpdate() {
        await this.$nextTick();
        this.sendValidation();

        if (this.isValid) {
          this.updateValue();
        }
      },
      sendValidation() {
        window.parent.postMessage(
          {
            mceAction: CUSTOM_BUTTON_EVENT_TYPES.VALIDATION,
            data: { isValid: this.isValid },
          },
          origin,
        );
      },
      updateValue() {
        window.parent.postMessage(
          {
            mceAction: CUSTOM_BUTTON_EVENT_TYPES.UPDATE_DATA,
            data: withCustomButtonDefaults({
              ...this.formData,
              text: this.formData.text.trim(),
              url: this.formData.url.trim(),
              secondaryText: this.formData.secondaryText.trim(),
            }),
          },
          origin,
        );
      },
      setColor(key, value) {
        this.formData[key] = value || CUSTOM_BUTTON_DEFAULTS[key];
      },
      updateIcon(photoData) {
        if (!photoData) {
          this.formData.customIconId = "";
          this.formData.customIconSrc = "";
          return;
        }

        this.formData.customIconId = photoData["@id"] || "";
        this.formData.customIconSrc = photoData.contentUrl || "";
      },
      setupReceiveDataListener() {
        window.onmessage = async message => {
          if (!message.data || typeof message.data !== "object") return;

          if (message.data.type === CUSTOM_BUTTON_EVENT_TYPES.NEED_VALIDATION) {
            this.validationRequested = true;
            if (this.$refs.form) this.$refs.form.validate();
            await this.validateAndUpdate();
          }

          if (message.data.type === CUSTOM_BUTTON_EVENT_TYPES.SEND_DATA) {
            this.formData = withCustomButtonDefaults(message.data.data);
            await this.$nextTick();
            await this.validateAndUpdate();
          }
        };
      },
      requestData() {
        window.parent.postMessage({ mceAction: CUSTOM_BUTTON_EVENT_TYPES.REQUEST_DATA }, origin);
      },
    },
  };
</script>

<style lang="scss">
  @import "@core/preset/preset/custom-button.scss";
</style>

<style lang="scss" scoped>
  .custom-button-dialog {
    display: flex;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 30px;
    padding: 30px 12px 0;
  }

  .edit-column {
    flex: 1 1 58%;
    min-width: 0;
  }

  .preview-column {
    position: sticky;
    top: 0;
    z-index: 2;
    flex: 1 1 42%;
    min-width: 0;
  }

  .section {
    margin-top: 24px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;

    &:first-child {
      margin-top: 0;
    }

    h4 {
      margin: 0;
      font-weight: 500;
    }
  }

  .preview {
    margin-top: 0;
    background: #fff;
  }

  @media (max-width: 720px) {
    .custom-button-dialog {
      flex-direction: column;
    }

    .preview-column {
      position: static;
      width: 100%;
    }
  }

  .custom-button__icon-slot {
    display: contents;
  }
</style>
